import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { GuideTourProvider } from "@/common/components/guide-tour/GuideTourContext";
import PageLoader from "@/common/components/PageLoader";
import { useAuth } from "@/common/hooks/useAuth";
import PaywallModal from "@/components/billing/PaywallModal";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { AdminRoute } from "@/routes/AdminRoute";
import { AuthOnlyRoute } from "@/routes/AuthOnlyRoute";
import { OrgAdminRoute } from "@/routes/OrgAdminRoute";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { PublicRoute } from "@/routes/PublicRoute";

const HomePage = lazy(() => import("./pages/public/HomePage"));
const SharedReportPage = lazy(() => import("./pages/public/SharedReportPage"));
const AuthWrapper = lazy(() => import("@/pages/auth/wrapper/AuthWrapper"));
const SignupWrapper = lazy(() => import("@/pages/auth/wrapper/SignupWrapper"));
const OnboardingStatus = lazy(() => import("./pages/auth/OnboardingStatus"));
const OnboardingWizard = lazy(() => import("./pages/auth/OnboardingWizard"));
const OnboardingPlans = lazy(() => import("./pages/auth/OnboardingPlans"));
const VerifyAccount = lazy(() => import("./pages/auth/VerifyAccount"));
const AcceptInvite = lazy(() => import("./pages/auth/AcceptInvite"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const SelectOrg = lazy(() => import("./pages/auth/SelectOrg"));
const BillingPage = lazy(() => import("./pages/billing/BillingPage"));
const PricingPage = lazy(() => import("./pages/billing/PricingPage"));
const PrivacyPolicyPage = lazy(
  () => import("./pages/public/PrivacyPolicyPage"),
);
const TermsOfServicePage = lazy(
  () => import("./pages/public/TermsOfServicePage"),
);
const PublicSupportPage = lazy(
  () => import("./pages/public/PublicSupportPage"),
);
const DowngradePage = lazy(() => import("./pages/billing/DowngradePage"));
const NotificationsPage = lazy(
  () => import("@/pages/notifications/NotificationsPage"),
);
const ProjectsAdminPage = lazy(() => import("./pages/admin/ProjectsAdminPage"));

const AdminRoutes = lazy(() => import("@/routes/modules/Admin"));
const OrgAdminRoutes = lazy(() => import("@/routes/modules/OrgAdmin"));
const DashboardRoutes = lazy(() => import("@/routes/modules/Dashboard"));
const Dashboard2Routes = lazy(() => import("@/routes/modules/Dashboard2"));
const TestCases = lazy(() => import("@/routes/modules/TestCases"));
const LoadTestRoutes = lazy(() => import("@/routes/modules/LoadTest"));
const ElementsRoute = lazy(() => import("@/routes/modules/Elements"));
const DefectRoute = lazy(() => import("@/routes/modules/DefectRoute"));
const TestDataRoute = lazy(() => import("@/routes/modules/TestDataRoute"));
const TestManagementRoute = lazy(
  () => import("@/routes/modules/TestManagement"),
);
const CyclesRoute = lazy(() => import("@/routes/modules/Cycles"));
const ReportRoute = lazy(() => import("@/routes/modules/Report"));
const UploadsRoute = lazy(() => import("@/routes/modules/Uploads"));
const AccessibilityRoute = lazy(
  () => import("@/routes/modules/Accessibility"),
);
const Settings = lazy(() => import("@/routes/modules/Settings"));
const DocumentationRoute = lazy(() => import("@/routes/modules/Documentation"));
const GuideMeRoute = lazy(() => import("@/routes/modules/GuideMe"));
const SupportRoute = lazy(() => import("@/routes/modules/Support"));
const LoadTestBuilderPage = lazy(
  () => import("@/pages/load-test-builder/LoadTestBuilderPage"),
);

function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function LegacyScenarioRedirect() {
  const { testCaseId } = useParams<{ testCaseId: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/testcases/${testCaseId}${location.search}`}
      replace
    />
  );
}

function SignupRedirect() {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const to = plan
    ? `/onboarding?plan=${encodeURIComponent(plan)}`
    : "/onboarding";
  return <Navigate to={to} replace />;
}

function RootRoute() {
  const { isAuthenticated, isSuperAdmin } = useAuth();
  if (!isAuthenticated) return <HomePage />;
  if (isSuperAdmin) return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  return (
    <Router>
      <GuideTourProvider>
        <ImpersonationBanner />
        <PaywallModal />
        <Suspense fallback={<PageLoader fullScreen />}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/contact/support" element={<PublicSupportPage />} />

            <Route path="/docs/*" element={<DocumentationRoute />} />

            <Route element={<OrgAdminRoute />}>
              <Route path="/admin/projects" element={<ProjectsAdminPage />} />
              <Route path="/org-admin/*" element={<OrgAdminRoutes />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="/admin/*" element={<AdminRoutes />} />
            </Route>

            <Route element={<AuthOnlyRoute />}>
              <Route path="/select-org" element={<SelectOrg />} />
              <Route path="/onboarding/wizard" element={<OnboardingWizard />} />
              <Route path="/onboarding/plans" element={<OnboardingPlans />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/downgrade" element={<DowngradePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/dashboard/*" element={<DashboardRoutes />} />
              <Route path="/dashboard2/*" element={<Dashboard2Routes />} />
              <Route path="/testcases/*" element={<TestCases />} />
              <Route
                path="/loadtest/builder"
                element={<LoadTestBuilderPage />}
              />
              <Route path="/loadtest/*" element={<LoadTestRoutes />} />
              <Route path="/elements/*" element={<ElementsRoute />} />
              <Route path="/defects/*" element={<DefectRoute />} />
              <Route path="/test-data/*" element={<TestDataRoute />} />
              <Route
                path="/test-management/*"
                element={<TestManagementRoute />}
              />
              <Route path="/cycles/*" element={<CyclesRoute />} />
              <Route path="/reports/*" element={<ReportRoute />} />
              <Route path="/uploads/*" element={<UploadsRoute />} />
              <Route
                path="/accessibility/*"
                element={<AccessibilityRoute />}
              />
              <Route path="/settings/*" element={<Settings />} />
              <Route path="/guideme/*" element={<GuideMeRoute />} />
              <Route path="/support/*" element={<SupportRoute />} />
              <Route
                path="/load-test"
                element={<RedirectWithSearch to="/loadtest/builder" />}
              />
              <Route
                path="/load-test/builder"
                element={<RedirectWithSearch to="/loadtest/builder" />}
              />

              {/* Aliases for legacy /scenarios/* URLs to preserve search params and prevent blank pages */}
              <Route
                path="/scenarios/all-scenarios/*"
                element={<RedirectWithSearch to="/testcases/all" />}
              />
              <Route
                path="/scenarios/defects/*"
                element={<RedirectWithSearch to="/defects" />}
              />
              <Route
                path="/scenarios/requirements/*"
                element={
                  <RedirectWithSearch to="/test-management/requirements" />
                }
              />
              <Route
                path="/scenarios/:testCaseId"
                element={<LegacyScenarioRedirect />}
              />
            </Route>

            <Route path="/signup" element={<SignupRedirect />} />

            {/* Public shared report — no auth required */}
            <Route
              path="/shared/report/:token"
              element={
                <Suspense fallback={<PageLoader fullScreen />}>
                  <SharedReportPage />
                </Suspense>
              }
            />

            <Route element={<PublicRoute />}>
              <Route path="/login" element={<AuthWrapper />} />
              <Route path="/onboarding" element={<SignupWrapper />} />
              <Route path="/onboarding/status" element={<OnboardingStatus />} />
              <Route
                path="/onboarding/verify-account/:token"
                element={<VerifyAccount />}
              />
            </Route>

            {/* Accept invite is fully public — must be accessible even when already logged in
              so the invitee can accept for a different account without being auto-redirected */}
            <Route
              path="/onboarding/accept-invite/:token"
              element={
                <Suspense fallback={<PageLoader fullScreen />}>
                  <AcceptInvite />
                </Suspense>
              }
            />

            {/* Password reset — must be accessible without a session */}
            <Route
              path="/auth/reset-password/:token"
              element={
                <Suspense fallback={<PageLoader fullScreen />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />
          </Routes>
        </Suspense>
      </GuideTourProvider>
    </Router>
  );
}

export default AppRouter;
